import isObject from '#shared/util/isObject.js';

/**
 * Cookie class with Web Storage compatible API
 * @class CookieStorage
 */
export default class CookieStorage {

	/**
	 * Init cookieStorage
	 */
	constructor() {
		this._helpers = {
			getCookieKeys() {
				return this.getCookies().split(';').map((item) => item.trim().split('=')[0]);
			},
			getPairString(key, value) {
				const keyString = encodeURIComponent(key);
				const valueString = encodeURIComponent(value);
				return `${keyString}=${valueString}`;
			},
			getExpiresString(expires) {
				if (typeof expires === 'number' || expires && (typeof expires === 'string' || typeof expires === 'object' && typeof expires.toUTCString === 'function')) {
					const currentDate = new Date();
					const currentTime = currentDate.getTime();
					const expirationDate = new Date(typeof expires === 'number' ? currentTime + expires : expires);
					const expirationTime = expirationDate.getTime();
					const maxAgeNumber = Math.ceil((expirationTime - currentTime) / 1000);
					return !Number.isNaN(expirationDate) && !Number.isNaN(expirationTime) ? `; expires=${expirationDate.toUTCString()}; max-age=${String(maxAgeNumber)}` : '';
				} else {
					return '';
				}
			},
			getDomainString(domain) {
				return typeof domain === 'string' && domain ? `; domain=${domain}` : '';
			},
			getPathString(path) {
				return typeof path === 'string' && path ? `; path=${path}` : '';
			},
			getSecureString(secure) {
				return secure ? '; secure' : '';
			},
			getRemoveString(key) {
				const keyString = encodeURIComponent(key);
				return `${keyString}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0`;
			}
		};
		const isCookieEnabled = (() => {
			if (isObject(navigator) && navigator.cookieEnabled) {
				return true;
			} else if (isObject(document) && typeof document.cookie === 'string') {
				const key = 'isCookieEnabled';
				const pair = this._helpers.getPairString(key, true);
				document.cookie = pair;
				if (document.cookie.includes(`${pair};`)) {
					document.cookie = this._helpers.getRemoveString(key); // join key and string for cookie removal
					return true;
				} else {
					return false;
				}
			} else {
				return false;
			}
		})();
		Object.assign(this._helpers, {
			fallback: '',
			isCookieEnabled,
			getCookies: isCookieEnabled ? () => document.cookie : () => this._helpers.fallback,
			setCookies: isCookieEnabled ? (cookieString) => {
				document.cookie = cookieString;
			} : (cookieString) => {
				this._helpers.fallback += cookieString;
			}
		});
	}

	/**
	 * Get storage length
	 * @public
	 * @memberof CookieStorage
	 * @returns {number}
	 */
	get length() {
		return this._helpers.getCookieKeys().length;
	}

	/**
	 * Set cookie
	 * @param {string} key Cookie key
	 * @param {string} value Cookie value
	 * @param {object} [options] Options object
	 * @param {Date|string} [options.expires] Cookie expires in / date of experiation
	 * @param {string} [options.domain] The domain to which the cookie applies
	 * @param {string} [options.path] The path pattern to which the cookie applies
	 * @param {boolean} [options.secure] Set cookie as secure
	 * @public
	 * @memberof CookieStorage
	 */
	setItem(key, value, { expires, domain, path, secure } = {}) { // httpOnly and SameSite attributes can only be set by server
		const regExp = /^(?:expires|max-age|domain|path|secure)$/i;
		if (typeof key !== 'undefined' && typeof value !== 'undefined' && !regExp.test(key)) {
			const helpers = this._helpers;
			helpers.setCookies(helpers.getPairString(key, value) + helpers.getExpiresString(expires) + helpers.getDomainString(domain) + helpers.getPathString(path) + helpers.getSecureString(secure)); // join key, value, expires, max-age, domain, path and secure option
		} else {
			throw new Error('Invalid method arguments to cookieStorage.setItem');
		}
	}

	/**
	 * Get cookie
	 * @param {string} key Cookie key
	 * @returns {string}
	 * @public
	 * @memberof CookieStorage
	 */
	getItem(key) {
		if (typeof key !== 'undefined') {
			const keyString = encodeURIComponent(key).replace(/[-.+*]/g, '\\$&');
			const regExp = new RegExp(`(?:(?:^|.*;)\\s*${keyString}\\s*\\=\\s*([^;]*).*$)|^.*$`);
			const valueString = this._helpers.getCookies().replace(regExp, '$1');
			return decodeURIComponent(valueString) || null;
		} else {
			throw new Error('Invalid method arguments to cookieStorage.getItem');
		}
	}

	/**
	 * Remove cookie
	 * @param {*} key Cookie key
	 * @param {object} [options] Options object
	 * @param {string} [options.domain] The domain to which the cookie applies
	 * @param {string} [options.path] The path pattern to which the cookie applies
	 * @public
	 * @memberof CookieStorage
	 */
	removeItem(key, { domain, path } = {}) {
		if (typeof key !== 'undefined') {
			const helpers = this._helpers;
			helpers.setCookies(helpers.getRemoveString(key) + helpers.getDomainString(domain) + helpers.getPathString(path)); // join key, string for cookie removal, domain and path
		} else {
			throw new Error('Invalid method arguments to cookieStorage.removeItem');
		}
	}

	/**
	 * Get name of the nth key
	 * @param {number} n Index
	 * @returns {string|null}
	 * @public
	 * @memberof CookieStorage
	 */
	key(n) {
		if (typeof n === 'number') {
			const keys = this._helpers.getCookieKeys();
			return n >= 0 && n < keys.length ? decodeURIComponent(keys[n]) : null;
		} else {
			throw new Error('Invalid method arguments to cookieStorage.key');
		}
	}

	/**
	 * Clear all cookies
	 * @public
	 * @memberof CookieStorage
	 */
	clear() {
		this._helpers.getCookieKeys().forEach((key) => this.removeItem(key));
	}

}
